import { useState, useEffect, useRef } from 'preact/hooks'
import { Send, Loader2, Reply } from 'lucide-preact'
import type { ApprovalCommentViewDto } from '@/api/asset'
import { PersonAvatar } from '@/components/common/Avatar'
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader'
import { shortAgo } from '@/utils/time'

interface AssetCommentThreadProps {
  comments: ApprovalCommentViewDto[]
  onAddComment: (text: string, parentCommentId?: string) => Promise<void>
  loading: boolean
  disabled?: boolean
  /** Collapsed state is owned by the panel, which divides the height between the two sections. */
  collapsed?: boolean
  onToggleCollapsed?: () => void
}

interface AssetCommentItemProps {
  comment: ApprovalCommentViewDto
  onReply?: (commentId: string, authorName: string) => void
}

function AssetCommentItem({ comment, onReply }: AssetCommentItemProps) {
  const nameParts = comment.AuthorName.split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  return (
    <div className="flex gap-2.5">
      <div className="flex-shrink-0 mt-0.5">
        <PersonAvatar
          firstName={firstName}
          lastName={lastName}
          profilePicUrl={comment.AuthorProfilePicUrl}
          size="sm"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {comment.AuthorName}
          </span>
          {comment.AuthorRole && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              {comment.AuthorRole}
            </span>
          )}
          {comment.AuthorCompanyName && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#fae8ff] dark:bg-[#701a75]/30 text-[#a21caf] dark:text-[#f0abfc]">
              {comment.AuthorCompanyName}
            </span>
          )}
          <span className="text-[11px] text-slate-400 dark:text-slate-500 whitespace-nowrap">
            {shortAgo(comment.CreatedAt)}
          </span>
        </div>
        <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2">
          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
            {comment.Text}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {comment.VersionNumber > 0 && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              v{comment.VersionNumber}
            </span>
          )}
          {onReply && (
            <button
              onClick={() => onReply(comment.CommentId, comment.AuthorName)}
              className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Reply
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CommentGroup({
  parent,
  replies,
  onReply,
}: {
  parent: ApprovalCommentViewDto
  replies: ApprovalCommentViewDto[]
  onReply?: (commentId: string, authorName: string) => void
}) {
  return (
    <div>
      <AssetCommentItem comment={parent} onReply={onReply} />
      {replies.length > 0 && (
        <div className="ml-8 mt-2 space-y-3 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
          {replies.map((reply) => (
            <AssetCommentItem key={reply.CommentId} comment={reply} />
          ))}
        </div>
      )}
    </div>
  )
}

export function AssetCommentThread({ comments, onAddComment, loading, disabled, collapsed = false, onToggleCollapsed }: AssetCommentThreadProps) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyingTo, setReplyingTo] = useState<{ commentId: string; authorName: string } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Separate top-level and replies, sort each by CreatedAt
  const topLevel = comments
    .filter((c) => !c.ParentCommentId)
    .sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime())

  const repliesByParent = new Map<string, ApprovalCommentViewDto[]>()
  for (const c of comments) {
    if (c.ParentCommentId) {
      const list = repliesByParent.get(c.ParentCommentId) || []
      list.push(c)
      repliesByParent.set(c.ParentCommentId, list)
    }
  }
  // Sort replies within each group
  for (const [, list] of repliesByParent) {
    list.sort((a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime())
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [comments.length])

  const handleReply = (commentId: string, authorName: string) => {
    setReplyingTo({ commentId, authorName })
    inputRef.current?.focus()
  }

  const handleCancelReply = () => {
    setReplyingTo(null)
  }

  const handleSubmit = async () => {
    const trimmed = text.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    try {
      await onAddComment(trimmed, replyingTo?.commentId)
      setText('')
      setReplyingTo(null)
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className={collapsed ? 'flex flex-col' : 'flex flex-col h-full'}>
      {/* The shared header, so this and Quality check cannot drift apart again (ORB-889). When the
          panel gives no toggle, it renders as the same static strip it always was. */}
      {onToggleCollapsed ? (
        <CollapsibleSectionHeader
          title="Comments"
          count={comments.length}
          collapsed={collapsed}
          onToggle={onToggleCollapsed}
        />
      ) : (
        <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            Comments {comments.length > 0 && `(${comments.length})`}
          </h3>
        </div>
      )}

      {collapsed ? null : (<>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading && comments.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
          </div>
        ) : topLevel.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">
            No comments yet. Start the conversation below.
          </p>
        ) : (
          topLevel.map((comment) => (
            <CommentGroup
              key={comment.CommentId}
              parent={comment}
              replies={repliesByParent.get(comment.CommentId) || []}
              onReply={disabled ? undefined : handleReply}
            />
          ))
        )}
      </div>

      {disabled ? (
        <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 px-4 py-3">
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center">
            Comments are locked for signed-off assets.
          </p>
        </div>
      ) : (
        <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 p-3">
          {replyingTo && (
            <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 dark:text-slate-400">
              <Reply className="w-3 h-3" />
              <span>Replying to <span className="font-medium text-slate-700 dark:text-slate-200">{replyingTo.authorName}</span></span>
              <button
                onClick={handleCancelReply}
                className="ml-auto text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={text}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              onKeyDown={handleKeyDown}
              placeholder={replyingTo ? `Reply to ${replyingTo.authorName}...` : 'Add a comment...'}
              rows={2}
              disabled={submitting}
              className="flex-1 resize-none rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-slate-100 px-3 py-2 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            />
            <button
              onClick={handleSubmit}
              disabled={!text.trim() || submitting}
              className="self-end inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
              title="Send comment"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}
      </>)}
    </div>
  )
}
